import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatDistanceToNow } from "date-fns";
import { IMessage, TImageFormat, TDocumentFormat } from "./types";

function getApiBaseUrl(): string {
    const env = process.env.NEXT_PUBLIC_ENV;
    if (env === "production") {
        return "https://ai-chat-api2.fendergarage.com";
    }
    if (env === "development") {
        return "https://active-ai-chat-api2.fendergarage.com";
    }
    return "http://localhost:4000";
}

export const API_BASE_URL = getApiBaseUrl();
export const GRAPHQL_API_URL = `${API_BASE_URL}/graphql`;
export const CHAT_API_URL = `${API_BASE_URL}/chat`;
export const HEALTH_API_URL = `${API_BASE_URL}/health`;
export const TITLE_WORD_LIMIT = parseInt(process.env.NEXT_PUBLIC_TITLE_WORD_LIMIT || "5", 10);

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function getImageFormat(file: File): TImageFormat | null {
    const mimeType = file.type.toLowerCase();
    if (mimeType === "image/png") return "png";
    if (mimeType === "image/jpeg" || mimeType === "image/jpg") return "jpeg";
    if (mimeType === "image/gif") return "gif";
    if (mimeType === "image/webp") return "webp";
    return null;
}

export function getDocumentFormat(file: File): TDocumentFormat | null {
    const fileName = file.name.toLowerCase();
    const extension = fileName.split(".").pop();

    if (extension === "pdf") return "pdf";
    if (extension === "csv") return "csv";
    if (extension === "doc") return "doc";
    if (extension === "docx") return "docx";
    if (extension === "xls") return "xls";
    if (extension === "xlsx") return "xlsx";
    if (extension === "html") return "html";
    if (extension === "txt") return "txt";
    if (extension === "md") return "md";
    return null;
}

export function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = (reader.result as string).split(",").at(1) ?? "";
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export function extractDomain(url: string): string {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname;
        const parts = hostname.split(".");

        if (parts.length >= 2) {
            return parts.slice(-2).join(".");
        }

        return hostname;
    } catch {
        return "";
    }
}

export function getFirstAssistantTextContent(messages: IMessage[]): string | undefined {
    const firstAssistantMessage = messages.find(
        (msg) => msg.role === "assistant" && msg.content.some((block) => block.text)
    );
    const textContent = firstAssistantMessage?.content.find((block) => block.text);
    return textContent?.text;
}

export function formatDate(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    // For very recent (< 1 minute), show "Just now"
    if (diffMs < 60000) {
        return "Just now";
    }

    // For all other times, show relative time (minutes, hours, days, months, years ago)
    const relativeTime = formatDistanceToNow(date, { addSuffix: true });
    // Capitalize first letter
    return relativeTime.charAt(0).toUpperCase() + relativeTime.slice(1);
}

export function generateThumbnail(file: File, size: number = 60): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new window.Image();
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        img.onload = () => {
            canvas.width = size;
            canvas.height = size;

            // Calculate center crop dimensions for square thumbnail
            const minDim = Math.min(img.width, img.height);
            const sx = (img.width - minDim) / 2;
            const sy = (img.height - minDim) / 2;

            ctx?.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);

            const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
            URL.revokeObjectURL(img.src);
            resolve(dataUrl);
        };

        img.onerror = () => {
            URL.revokeObjectURL(img.src);
            reject(new Error("Failed to load image for thumbnail generation"));
        };

        img.src = URL.createObjectURL(file);
    });
}

export const THINKING_VARIATIONS: string[] = [
    "Thinking...",
    "Working...",
    "Processing...",
    "Pondering...",
    "Analyzing...",
    "Computing...",
    "Contemplating...",
    "Deliberating...",
    "Considering...",
    "Reflecting...",
    "Reasoning...",
    "Figuring out...",
    "Mulling over...",
    "Crunching...",
    "Calculating...",
    "Evaluating...",
    "Assessing...",
    "Examining...",
    "Investigating...",
    "Researching...",
    "Formulating...",
    "Synthesizing...",
    "Generating...",
    "Crafting...",
    "Preparing...",
    "Composing...",
    "Constructing...",
    "Building...",
    "Assembling...",
    "On it...",
    "Brewing...",
    "Cooking up...",
    "Conjuring...",
    "Spinning up...",
    "Digging in...",
    "Getting there...",
    "Almost ready...",
    "Hang tight...",
    "Bear with me...",
    "One moment...",
    "Just a sec...",
    "Loading thoughts...",
    "Gears turning...",
    "Neurons firing...",
    "Brainstorming...",
    "Cogitating...",
    "Ruminating...",
    "Ideating...",
    "Noodling...",
    "Working on it...",
    "Chugging along...",
    "Piecing together...",
    "Putting it together...",
    "Mapping it out...",
    "Sorting through...",
    "Sifting through...",
    "Weighing options...",
    "Connecting dots...",
    "Running the numbers...",
    "Churning away...",
    "Hatching ideas...",
    "Percolating...",
    "Marinating...",
    "Stewing on it...",
    "Chewing on it...",
    "Digesting...",
    "Unwrapping...",
    "Unpacking...",
    "Decoding...",
    "Deciphering...",
    "Parsing...",
    "Distilling...",
    "Refining...",
    "Polishing...",
    "Fine-tuning...",
    "Calibrating...",
    "Optimizing...",
    "Tinkering...",
    "Tweaking...",
    "Iterating...",
    "Exploring...",
    "Diving deep...",
    "Drilling down...",
    "Zooming in...",
    "Honing in...",
    "Zeroing in...",
    "Wrangling...",
    "Tackling...",
    "Hammering away...",
    "Plugging away...",
    "Grinding...",
    "Busy busy...",
    "Hard at work...",
    "In the zone...",
    "Deep in thought...",
    "Mind at work...",
    "Working 9 to 5...",
    "Working for the weekend...",
    "Workin' day and night...",
    "Taking care of business...",
    "Working my way back to you...",
    "Thinking out loud...",
    "Don't think twice...",
    "Work it...",
    "Let me work it...",
    "Put in work...",
    "Waiting on the world to change...",
    "Waiting for tonight...",
    "Waiting in vain...",
    "Waiting for a star to fall...",
    "The waiting is the hardest part...",
    "Right here waiting...",
    "Waiting for love...",
    "Waiting on a sunny day...",
    "...Ready for it?",
    "Long story short...",
];

export function getRandomThinkingVariation(): string {
    return THINKING_VARIATIONS[Math.floor(Math.random() * THINKING_VARIATIONS.length)];
}
